import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Gift, 
  ArrowRight, 
  Sparkles, 
  Clock,
  TrendingUp,
  Star,
  Zap,
  RefreshCw,
  Timer
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Countdown Timer Component
const CountdownTimer = ({ endDate, t }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const difference = end - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  if (isExpired) {
    return (
      <div className="flex items-center gap-2 text-red-400 text-sm">
        <Clock className="w-4 h-4" />
        <span>{t('campaigns.expired')}</span>
      </div>
    );
  }

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="bg-gradient-to-b from-red-500/30 to-red-600/20 border border-red-500/40 rounded-lg px-2 py-1 min-w-[40px]">
        <span className="text-lg sm:text-xl font-bold text-white font-mono">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-[10px] text-red-400/80 mt-1 uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 border border-red-500/30 rounded-xl p-3 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <Timer className="w-4 h-4 text-red-400 animate-pulse" />
        <span className="text-xs text-red-400 font-semibold uppercase tracking-wider">
          {t('campaigns.endsIn')}
        </span>
      </div>
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        <TimeBlock value={timeLeft.days} label={t('campaigns.days')} />
        <span className="text-red-400 font-bold text-lg">:</span>
        <TimeBlock value={timeLeft.hours} label={t('campaigns.hours')} />
        <span className="text-red-400 font-bold text-lg">:</span>
        <TimeBlock value={timeLeft.minutes} label={t('campaigns.minutes')} />
        <span className="text-red-400 font-bold text-lg">:</span>
        <TimeBlock value={timeLeft.seconds} label={t('campaigns.seconds')} />
      </div>
    </div>
  );
};

export default function Campaigns() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/campaigns`);
      setCampaigns(response.data.campaigns || []);
      
      // Mark campaigns as seen
      await axios.post(`${API_URL}/campaigns/mark-seen`);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleDepositClick = (campaign) => {
    // Navigate to deposit with pre-loaded amount
    navigate(`/deposit?amount=${campaign.deposit_amount}&campaign=${campaign.id}`);
  };

  const getBonusPercentage = (campaign) => {
    return Math.round((campaign.bonus_amount / campaign.deposit_amount) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="campaigns-loading">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="campaigns-page">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-4">
          <Gift className="w-8 h-8 text-amber-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('campaigns.title')}</h1>
        <p className="text-slate-400 max-w-md mx-auto">
          {t('campaigns.subtitle')}
        </p>
      </div>

      {/* Campaigns Grid */}
      {campaigns.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
          <Sparkles className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">{t('campaigns.noCampaigns')}</h3>
          <p className="text-slate-400">{t('campaigns.checkBackSoon')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {campaigns.map((campaign, index) => (
            <div 
              key={campaign.id}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 hover:border-emerald-500/30 transition-all duration-300"
              data-testid={`campaign-card-${index}`}
            >
              {/* Background Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Badge */}
              {campaign.badge_text && (
                <div className="absolute top-4 right-4 z-10">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold uppercase tracking-wider">
                    <Zap className="w-3 h-3" />
                    {campaign.badge_text}
                  </span>
                </div>
              )}

              <div className="relative p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                  {/* Left: Campaign Info */}
                  <div className="flex-1">
                    {/* Bonus Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-semibold mb-4">
                      <TrendingUp className="w-4 h-4" />
                      {t('campaigns.bonusPercentage', { percentage: getBonusPercentage(campaign) })}
                    </div>

                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                      {campaign.title}
                    </h2>
                    <p className="text-slate-400 mb-4">
                      {campaign.description}
                    </p>

                    {/* Countdown Timer */}
                    {campaign.end_date && (
                      <CountdownTimer endDate={campaign.end_date} t={t} />
                    )}
                  </div>

                  {/* Right: Amount Cards + CTA */}
                  <div className="lg:w-80">
                    {/* Amount Display */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {/* Deposit Amount */}
                      <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('campaigns.deposit')}</p>
                        <p className="text-2xl font-bold text-white">{formatCurrency(campaign.deposit_amount)}</p>
                      </div>
                      
                      {/* Bonus Amount */}
                      <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-4 text-center border border-emerald-500/20">
                        <p className="text-xs text-emerald-400 uppercase tracking-wider mb-1">{t('campaigns.receive')}</p>
                        <p className="text-2xl font-bold text-emerald-400">+{formatCurrency(campaign.bonus_amount)}</p>
                      </div>
                    </div>

                    {/* Total Display */}
                    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-3 mb-4 border border-amber-500/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-400" />
                          <span className="text-sm text-slate-300">{t('campaigns.totalInAccount')}</span>
                        </div>
                        <span className="text-lg font-bold text-amber-400">
                          {formatCurrency(campaign.deposit_amount + campaign.bonus_amount)}
                        </span>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handleDepositClick(campaign)}
                      className="w-full h-12 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all duration-300 group/btn"
                      data-testid={`campaign-deposit-btn-${index}`}
                    >
                      <span>{t('campaigns.depositNow')}</span>
                      <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
            </div>
          ))}
        </div>
      )}

      {/* Footer Note */}
      <div className="mt-10 text-center">
        <p className="text-slate-500 text-sm">
          {t('campaigns.footerNote')}
        </p>
      </div>
    </div>
  );
}
